/* The pieces of mockups/crm-v3-campaigns-v1.html §#campaigns and §#sent that
   the Contacts and Clients tables do not already draw: the approval bar, the
   email preview, the versions row, the results tiles, and the 420px drawer
   with its full-width mode.

   Everything else comes from listStyles.js — the filter row, the table box,
   the drawer chrome and the footer are the same furniture, and this file
   deliberately does not copy them.

   Colours are tokens from src/lib/styles.js. Where the mockup shows a tinted
   pill (`.p-review`, `.p-sent`) that is the token at 8% over the surface, the
   same recipe as `badge()` in styles.js.                                    */
import { C, font, mono } from "../../lib/styles.js";

/** The drawer is wider here than on Contacts (420 vs 380): a campaign carries
 *  a brief, an email and a results panel, and 380 wraps the approval line onto
 *  four lines. `full` is Ivin's "Open full" — the table goes and the drawer
 *  takes the content width. */
export const campaignLayout = (drawerOpen, isMobile, full) => ({
  display: "grid",
  gridTemplateColumns: full || !drawerOpen || isMobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) 420px",
  gap: 14,
  alignItems: "start",
});

export const CS = {
  /* ── the running check-ins row ── */
  runningName: { fontSize: 13, fontWeight: 400 },

  /* ── status pill: the token at 8%, like badge() ── */
  pill: (color) => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 10,
    fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.08em",
    background: `${color}14`, color, fontFamily: font, whiteSpace: "nowrap",
  }),

  /* ── origin, mono; purple when the chat wrote it ── */
  origin: { fontFamily: mono, fontSize: 10.5, color: C.muted },
  originChat: { fontFamily: mono, fontSize: 10.5, color: C.purple },

  /* ── the results cell: "41% opened ▓▓▓░ 7 replies" ── */
  res: { display: "inline-flex", alignItems: "center", gap: 8, fontFamily: mono, fontSize: 11.5, whiteSpace: "nowrap" },
  resBar: {
    display: "inline-block", height: 6, width: 56, borderRadius: 2,
    background: C.surfaceAlt, position: "relative", overflow: "hidden",
  },
  resFill: { position: "absolute", left: 0, top: 0, bottom: 0, background: C.stone, borderRadius: 2 },
  resNone: { fontFamily: mono, fontSize: 12, color: C.muted, opacity: 0.6 },

  /* ── the approval bar ── */
  approve: {
    background: C.bg, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`,
    borderRadius: 6, padding: "10px 12px", fontFamily: font,
  },
  approveWarn: {
    fontFamily: mono, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
    color: C.capture, fontWeight: 500, marginBottom: 4,
  },
  approveLine: { fontSize: 12.5, lineHeight: 1.5 },
  approveRow: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 8 },
  approveErr: {
    marginTop: 8, fontSize: 12, color: C.danger, lineHeight: 1.5, overflowWrap: "anywhere",
  },
  approveBusy: { marginTop: 8, fontSize: 12, color: C.muted },
  /* The schedule picker, drawn as one pill: "Send  Tue Sep 9, 9:40 am". */
  sel: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 9px", borderRadius: 5,
    fontSize: 11, color: C.muted, border: `1px solid ${C.border}`, background: C.surface, fontFamily: font,
  },
  selInput: {
    border: "none", background: "transparent", color: C.text, fontFamily: font,
    fontSize: 11, outline: "none", padding: 0, colorScheme: "light",
  },

  /* ── the email preview ── */
  mail: {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, fontFamily: font,
  },
  mailSubject: { fontWeight: 400, marginBottom: 6 },
  mailBody: { whiteSpace: "pre-wrap", overflowWrap: "anywhere" },
  mailCta: {
    display: "inline-block", marginTop: 8, padding: "5px 10px", borderRadius: 4,
    background: C.text, color: "#FFF", fontSize: 11, textDecoration: "none",
  },
  ver: { display: "flex", gap: 6, marginTop: 8, alignItems: "center", fontSize: 11, color: C.muted, flexWrap: "wrap" },
  verTag: {
    fontFamily: mono, fontSize: 10, border: `1px solid ${C.border}`, borderRadius: 4,
    padding: "1px 6px", background: "transparent", color: C.muted, cursor: "default",
  },
  verTagOn: { background: C.text, color: "#FFF", borderColor: "transparent" },

  /* ── the edit form ── */
  field: { marginBottom: 8 },
  fieldLabel: {
    fontFamily: mono, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
    color: C.muted, opacity: 0.85, display: "block", marginBottom: 4,
  },

  /* ── evidence + replies list ── */
  ev: { listStyle: "none", fontSize: 12.5, margin: 0, padding: 0 },
  evItem: {
    padding: "5px 0", borderBottom: `1px solid ${C.border}`,
    display: "grid", gridTemplateColumns: "14px minmax(0,1fr)", gap: 8, alignItems: "start",
  },
  evDot: { width: 7, height: 7, borderRadius: "50%", background: C.stone, display: "inline-block", marginTop: 6 },
  evDotLive: { background: C.green },
  evLink: {
    color: C.text, background: "transparent", border: "none", padding: 0, cursor: "pointer",
    borderBottom: `1px solid ${C.border}`, fontFamily: font, fontSize: 12.5, textAlign: "left",
  },

  /* ── the member list behind "See all N" ── */
  members: { listStyle: "none", fontSize: 12.5, margin: "8px 0 0", padding: 0, maxHeight: 320, overflowY: "auto" },
  member: {
    display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 10,
    padding: "5px 0", borderBottom: `1px solid ${C.border}`, alignItems: "center",
  },
  memberMeta: { fontSize: 11.5, color: C.muted, overflowWrap: "anywhere" },
  memberTag: { fontFamily: mono, fontSize: 10.5, color: C.muted, whiteSpace: "nowrap" },
};
